// Direct import of selector module needed instead of useBrand hook in non-React context where hooks cannot be used.
// eslint-disable-next-line @vio/custom-rules/prefer-usebrand-hook
import {getMetaBrandHasRooms} from 'modules/accommodation/selectors'
import {markAccommodationPageLoad} from 'modules/accommodation/slice'
import {
  getAnonymousId,
  getCurrencyCode,
  getLocaleCode
} from 'modules/meta/selectors'
import {searchContinued} from 'modules/sapiSearch/actions'
import {
  getAnchorHotelId,
  getHotelCountWithPromos,
  getIsAnchorHotelAvailable,
  getSearchId,
  getSearchParameters
} from 'modules/sapiSearch/selectors'
import {
  anchorOffersReceived,
  anchorReceived,
  hotelsAvailabilityCompleted,
  hotelsAvailabilityReceived,
  hotelsReceived,
  offersReceived,
  searchCompleted,
  searchStarted,
  setNextPage
} from 'modules/sapiSearch/slice'
import {getIsAnchorHotelHidden} from 'modules/search/selectors'
import {
  getFilteredRoomsData,
  getIsRoomsResultEmpty
} from 'modules/searchApi/selectors'
import {once, path} from 'ramda'
import {Dispatch} from 'redux'

import {
  getBrand,
  getBrandName
} from '@vio/core/src/_web/brand/modules/selectors'
import {
  logTimeEnd,
  logTimeStart
} from '@vio/core/src/_web/utils/logging/performanceTimer'
import {runInIdleTime} from '@vio/core/src/_web/utils/performance/idleUntilUrgent'
import {yieldToMain} from '@vio/core/src/_web/utils/performance/yieldToMain'
import {
  TRACK_ERROR_TRIGGERED,
  TRACK_EXPERIMENT_SERVED,
  TRACK_USER_ACTION,
  trackEvent,
  TrackProviderRedirectParams
} from '@vio/core/src/analytics/modules/actions'
import {trackProviderRedirect} from '@vio/core/src/analytics/modules/actions'
import {trackFullStoryEvent} from '@vio/core/src/analytics/modules/middleware'
import {customIdentify} from '@vio/core/src/analytics/modules/middleware/customIdentify'
import {customerIo} from '@vio/core/src/analytics/services/CustomerIo'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Page,
  Team,
  TrackEventPayload,
  TrackEventProperties
} from '@vio/core/src/analytics/types/Events'
import AnalyticsUtils, {isGoodBot} from '@vio/core/src/analytics/utils'
import {clearObjectArraysFromAnalyticsContext} from '@vio/core/src/analytics/utils/trackEventHelpers'
import {
  createDataPipelineEventName,
  removeSearchResultsContext
} from '@vio/core/src/analytics/utils/trackEventHelpers'
import {
  getCognitoIsMemberPlus,
  getIsPrivacyRestrictedUser,
  getUser,
  getUserEmail,
  getUserId,
  getUserIdentifiableTraits
} from '@vio/core/src/auth/modules/selectors'
import {identifyAuthenticatedUser} from '@vio/core/src/auth/modules/slice'
import {fetchOptimizelyDataFile} from '@vio/core/src/experiments/modules/actions'
import {getIsGhaChecker} from '@vio/core/src/rateChecker/selectors'
import {getTimezone} from '@vio/core/src/utils/date'
import {logger} from '@vio/core/src/utils/logging/logger'

import {MiddlewareType} from '../middlewareList'
import {
  trackAccommodationPageLoad,
  trackAccommodationPageReady,
  trackInteractiveLocationMapBoxDisplayed,
  trackInteractiveLocationMapBoxLoaded,
  trackMapBoxLoaded,
  trackMapViewDisplayed,
  trackStaticLocationMapBoxDisplayed,
  trackStaticLocationMapBoxLoaded,
  trackSystemSearchSucceeded
} from './actions/trackEvent'
import {
  getOfferClickedMetaData,
  TRACK_LOCKED_OFFER_CLICKED,
  TRACK_OFFER_CLICKED
} from './actions/trackOfferClicked'
import {TRACK_CIO_PAGE_VIEW, TRACK_PAGE_VIEW} from './actions/trackPageView'
import initializeAnalytics from './initializeAnalytics'
import {
  getCIOSearchContext,
  getEventProperties,
  getPaginationContext,
  getProviderRedirectParams,
  getSearchContext,
  getSearchResultsContext,
  getUserParametersContext
} from './selectors'
import {getTrackHotelsSearchedParamsForDD} from './utils'
import {logEvent} from './utils/logEvent'
import {
  getRemarketingPageViewParams,
  getSapiOfferClickedRemarketingParams
} from './utils/remarketing'

interface AnalyticsConfig {
  endpoint: string
  apiKey: string
}

// Next.js inlines environment variables into the build ONLY by the full `process.env.KEY`
// We cannot use dynamic lookups from process.env here
const ANALYTICS_CONFIGS: Partial<Record<Team, AnalyticsConfig>> = {
  [Team.Default]: {
    endpoint: process.env.NEXT_PUBLIC_ANALYTICS_DEFAULT_ENDPOINT,
    apiKey: process.env.NEXT_PUBLIC_ANALYTICS_DEFAULT_API_KEY
  },
  [Team.Checkout]: {
    endpoint: process.env.NEXT_PUBLIC_ANALYTICS_CHECKOUT_ENDPOINT,
    apiKey: process.env.NEXT_PUBLIC_ANALYTICS_CHECKOUT_API_KEY
  },
  [Team.Retention]: {
    endpoint: process.env.NEXT_PUBLIC_ANALYTICS_RETENTION_ENDPOINT,
    apiKey: process.env.NEXT_PUBLIC_ANALYTICS_RETENTION_API_KEY
  },
  [Team.Select]: {
    endpoint: process.env.NEXT_PUBLIC_ANALYTICS_SELECT_ENDPOINT,
    apiKey: process.env.NEXT_PUBLIC_ANALYTICS_SELECT_API_KEY
  },
  [Team.PowerFeatures]: {
    endpoint: process.env.NEXT_PUBLIC_ANALYTICS_POWER_FEATURES_ENDPOINT,
    apiKey: process.env.NEXT_PUBLIC_ANALYTICS_POWER_FEATURES_API_KEY
  },
  [Team.Shared]: {
    endpoint: process.env.NEXT_PUBLIC_ANALYTICS_SHARED_ENDPOINT,
    apiKey: process.env.NEXT_PUBLIC_ANALYTICS_SHARED_API_KEY
  },
  // Data reads System_Attribution_Changed from the Segment stream, not fe-evas
  [Team.Attribution]: {
    endpoint: process.env.NEXT_PUBLIC_ANALYTICS_ENDPOINT,
    apiKey: process.env.NEXT_PUBLIC_ANALYTICS_API_KEY
  }
}

/**
 * Reset the Segment analytics endpoint to the default endpoint after the endpoint was set to the team endpoint.
 * This is to avoid the analytics data to be sent to the wrong endpoint. All events that do not belong to a specific
 * team will be sent to the endpoint which is set by the following function.
 */
const resetSegmentEndpoint = () => {
  const ANALYTICS_ENDPOINT = process.env.NEXT_PUBLIC_ANALYTICS_ENDPOINT
  const API_KEY = process.env.NEXT_PUBLIC_ANALYTICS_API_KEY
  AnalyticsUtils.overrideSegmentDestinationSettings(ANALYTICS_ENDPOINT, API_KEY)
}

/**
 * In order to send each team events to a different endpoint, we need to override the segment endpoint by the team
 * endpoint which is defined in the environment variables. This function overrides the Segment endpoint with the
 * team endpoint.
 * @param - team the team that the event is being sent by
 */
const setSegmentTeamEndpoint = (team: Team) => {
  const config = ANALYTICS_CONFIGS[team]

  if (!config || !config.endpoint || !config.apiKey) {
    throw new Error(
      `Missing analytics endpoint and API key environment variable for team: ${team}`
    )
  }

  AnalyticsUtils.overrideSegmentDestinationSettings(
    config.endpoint,
    config.apiKey
  )
}

const trackAnalyticsEvent = (
  eventName: string,
  metadata: TrackEventProperties
) => {
  try {
    const {team, ...metadataWithoutTeam} = metadata
    setSegmentTeamEndpoint(
      team || (isBookingManagementRoute() ? Team.Checkout : Team.Default)
    )
    AnalyticsUtils.trackEvent(eventName, metadataWithoutTeam)
    resetSegmentEndpoint()
  } catch (error) {
    logger.error(error)
  }
}

const trackPageView = function (...args) {
  runInIdleTime(() => {
    AnalyticsUtils.trackPageView?.(...args)
  })
  const [pageName] = args as [string]
  logEvent(`PageView.${pageName}`)
}

const METRICS_WITH_EMPTY_SEARCH_CONTEXT = ['FetchOptimizelyDataFile']

const getTrackTimingEventPayload = (
  metric,
  duration,
  entity = Entity.Search,
  team = Team.Default
) => ({
  category: Category.System,
  entity,
  action: Action.PerformanceMeasured,
  analyticsContext: {
    [AnalyticsContext.PerformanceMeasuredContext]: {
      metric,
      value: duration,
      unit: 'ms'
    },
    ...(METRICS_WITH_EMPTY_SEARCH_CONTEXT.includes(metric) && {
      [AnalyticsContext.SearchContext]: {}
    })
  },
  team
})

export const trackTimingEvent = (
  dispatch: Dispatch,
  metric: string,
  fromMetric: string,
  entity: Entity = Entity.Search,
  team = Team.Default,
  shouldResetStartMark = false,
  payload?: TrackEventPayload['payload']
) => {
  const metricName = `${entity}-${metric}`
  const duration = logTimeEnd(metricName, fromMetric, shouldResetStartMark)
  if (!duration) return
  const event = getTrackTimingEventPayload(metric, duration, entity, team)

  dispatch(trackEvent({...event, payload}))
}

const trackPageViewTiming = once((dispatch: Dispatch) => {
  trackTimingEvent(dispatch, 'PageView', 'responseStart')
})

const trackHotelsSearchedTiming = once((dispatch: Dispatch) => {
  trackTimingEvent(dispatch, 'HotelsSearched', `PageView-start`)
  trackTimingEvent(dispatch, 'HotelsSearchedAbsoluteTime', 'responseStart')
})

const trackFirstHotelsReceivedTiming = once((dispatch: Dispatch) => {
  trackTimingEvent(dispatch, 'FirstHotelsReceived', 'HotelsSearched-start')
  trackTimingEvent(dispatch, 'FirstHotelsReceivedAbsoluteTime', 'responseStart')
})
const trackFirstAnchorHotelReceivedTiming = once((dispatch: Dispatch) => {
  trackTimingEvent(dispatch, 'FirstAnchorHotelReceived', 'HotelsSearched-start')
  trackTimingEvent(
    dispatch,
    'FirstAnchorHotelReceivedAbsoluteTime',
    'responseStart'
  )
})

const trackFirstAnchorOffersReceivedTiming = once((dispatch: Dispatch) => {
  trackTimingEvent(
    dispatch,
    'FirstAnchorOffersReceived',
    'HotelsSearched-start'
  )
  trackTimingEvent(
    dispatch,
    'FirstAnchorOffersReceivedAbsoluteTime',
    'responseStart'
  )
})

const trackFirstOffersReceivedTiming = once((dispatch: Dispatch) => {
  trackTimingEvent(dispatch, 'FirstOffersReceived', 'HotelsSearched-start')
  trackTimingEvent(dispatch, 'FirstOffersReceivedAbsoluteTime', 'responseStart')
})

const trackAllAnchorOffersReceivedTiming = once((dispatch: Dispatch) => {
  trackTimingEvent(dispatch, 'AllAnchorOffersReceived', 'HotelsSearched-start')
  trackTimingEvent(
    dispatch,
    'AllAnchorOffersReceivedAbsoluteTime',
    'responseStart'
  )
})

const trackAllOffersReceivedTiming = (dispatch: Dispatch) => {
  trackTimingEvent(dispatch, 'AllOffersReceived', 'HotelsSearched-start')
}

const trackAbsoluteAllOffersReceivedTiming = once((dispatch: Dispatch) => {
  trackTimingEvent(dispatch, 'AllOffersReceivedAbsoluteTime', 'responseStart')
})

const trackMapBoxLoadedTiming = (dispatch: Dispatch) => {
  trackTimingEvent(dispatch, 'MapBoxLoaded', `MapView-start`, Entity.MapView)
}

const trackAnchorHotelFirstAvailableDatesTiming = once((dispatch: Dispatch) => {
  trackTimingEvent(
    dispatch,
    'AnchorHotelFirstAvailableDates',
    'HotelsSearched-start'
  )
  trackTimingEvent(
    dispatch,
    'AnchorHotelFirstAvailableDatesAbsoluteTime',
    'responseStart'
  )
})

const trackAnchorHotelAllAvailableDatesTiming = once((dispatch: Dispatch) => {
  trackTimingEvent(
    dispatch,
    'AnchorHotelAllAvailableDates',
    'HotelsSearched-start'
  )
  trackTimingEvent(
    dispatch,
    'AnchorHotelAllAvailableDatesAbsoluteTime',
    'responseStart'
  )
})

const trackStaticLocationMapBoxLoadedTiming = (
  dispatch: Dispatch,
  team: Team
) => {
  trackTimingEvent(
    dispatch,
    'StaticLocationMapBoxLoaded',
    `StaticLocationMapView-start`,
    Entity.MapView,
    team
  )
}

const trackInteractiveLocationMapBoxLoadedTiming = (
  dispatch: Dispatch,
  team: Team
) => {
  trackTimingEvent(
    dispatch,
    'InteractiveLocationMapBoxLoaded',
    `InteractiveLocationMapView-start`,
    Entity.MapView,
    team
  )
}

const isBookingManagementRoute = () =>
  location && location.pathname.startsWith('/bookings')

/* eslint-disable complexity */
const middleware: MiddlewareType = store => next => async action => {
  const state = store.getState()
  const anonymousId = getAnonymousId(state)
  const {dispatch} = store
  const brand = getBrand(state)
  const cio = customerIo(brand)

  if (!AnalyticsUtils.analyticsPlatformEnabled()) {
    initializeAnalytics(anonymousId, brand)
  }

  if (
    !AnalyticsUtils.analyticsPlatformEnabled() ||
    isGoodBot(navigator.userAgent)
  ) {
    return next(action)
  }

  switch (action.type) {
    case TRACK_PAGE_VIEW: {
      await yieldToMain()

      const parameters = {
        ...action.params,
        ...getRemarketingPageViewParams(action.pageName),
        currency: getCurrencyCode(state),
        locale: getLocaleCode(state),
        // TODO: Deprecate
        timestampFirstByte: path(
          ['performance', 'timing', 'responseStart'],
          window
        )
      }

      trackPageView(action.pageName, parameters)
      logTimeStart('PageView')
      trackPageViewTiming(dispatch)

      if (action.pageName === Page.Search) {
        // SRP Ready - START
        logTimeStart('SRPReady')
      }

      break
    }

    case TRACK_CIO_PAGE_VIEW: {
      await yieldToMain()

      cio.trackPageView({
        pageName: action.pageName,
        userId: action.params.id,
        anonymousId,
        ...action.params,
        timezone: getTimezone()
      })

      break
    }

    case TRACK_OFFER_CLICKED: {
      await yieldToMain()

      const offerClickedMetaData = getOfferClickedMetaData(state, action.params)
      const offerClickedRemarketingParams =
        getSapiOfferClickedRemarketingParams(state, action.params)

      // TODO: Search TS preexisting issue
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      AnalyticsUtils.trackEvent('OfferClicked', {
        ...offerClickedMetaData,
        ...offerClickedRemarketingParams
      })
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      logEvent('OfferClicked', offerClickedMetaData)
      break
    }

    case TRACK_LOCKED_OFFER_CLICKED: {
      const offerClickedMetaData = getOfferClickedMetaData(state, action.params)

      // TODO: Search TS preexisting issue
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      logEvent('LockedOfferClicked', offerClickedMetaData)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      AnalyticsUtils.trackEvent('LockedOfferClicked', offerClickedMetaData)

      break
    }

    case searchStarted.type:
    case searchContinued.type: {
      const hotelsSearchedParameters = getTrackHotelsSearchedParamsForDD(
        state,
        action
      )

      // TODO: Search TS preexisting issue
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      logEvent('HotelsSearched', hotelsSearchedParameters)
      logTimeStart('HotelsSearched')
      trackHotelsSearchedTiming(dispatch)

      cio.trackEvent({
        name: `${Category.System}_${Entity.Search}_${Action.Requested}`,
        [AnalyticsContext.SearchContext]: {
          ...getCIOSearchContext(state),
          searchId: action.payload.searchId
        },
        [AnalyticsContext.UserParametersContext]:
          getUserParametersContext(state)
      })

      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Search,
          action: Action.Requested,
          analyticsContext: {
            [AnalyticsContext.PaginationContext]: getPaginationContext(state),
            [AnalyticsContext.SearchContext]: getSearchContext(
              state,
              action.payload.searchId
            )
          }
        })
      )
      break
    }

    case setNextPage.type: {
      // Treat SAPI page changes as a new search from tracking perspective
      const hotelsSearchedParameters = getTrackHotelsSearchedParamsForDD(
        state,
        {
          payload: {
            searchId: getSearchId(state),
            searchParameters: getSearchParameters(state)
          }
        }
      )

      // TODO: Search TS preexisting issue
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      logEvent('HotelsSearched', hotelsSearchedParameters)
      logTimeStart('HotelsSearched')

      cio.trackEvent({
        name: `${Category.System}_${Entity.Search}_${Action.Requested}`,
        [AnalyticsContext.SearchContext]: getCIOSearchContext(state),
        [AnalyticsContext.UserParametersContext]:
          getUserParametersContext(state)
      })

      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Search,
          action: Action.Requested,
          analyticsContext: {
            [AnalyticsContext.PaginationContext]: getPaginationContext(state)
          }
        })
      )
      break
    }

    case anchorReceived.type: {
      if (action.payload.anchorHotelId) {
        trackFirstAnchorHotelReceivedTiming(dispatch)
      }
      break
    }

    case hotelsReceived.type: {
      trackFirstHotelsReceivedTiming(dispatch)
      break
    }

    case anchorOffersReceived.type: {
      trackFirstAnchorOffersReceivedTiming(dispatch)
      break
    }

    case offersReceived.type: {
      const anchorHotelId = getAnchorHotelId(state)
      const {hotelOfferEntities, status} = action.payload

      if (
        hotelOfferEntities &&
        Object.keys(hotelOfferEntities)?.some(key => key !== anchorHotelId)
      ) {
        trackFirstOffersReceivedTiming(dispatch)
      }

      if (status?.anchorComplete) {
        trackAllAnchorOffersReceivedTiming(dispatch)
      }
      break
    }

    case searchCompleted.type: {
      trackAllOffersReceivedTiming(dispatch)
      trackAbsoluteAllOffersReceivedTiming(dispatch)

      dispatch(
        trackSystemSearchSucceeded({
          searchResultsContext: getSearchResultsContext(state)
        })
      )

      break
    }

    case trackSystemSearchSucceeded.type: {
      if (!getIsGhaChecker(state)) {
        await yieldToMain()
      }

      cio.trackEvent({
        name: `${Category.System}_${Entity.Search}_${Action.Succeeded}`,
        [AnalyticsContext.SearchContext]: getCIOSearchContext(state),
        [AnalyticsContext.UserParametersContext]:
          getUserParametersContext(state)
      })

      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Search,
          action: Action.Succeeded,
          payload: {
            isAnchorHotelAvailable: getIsAnchorHotelAvailable(state),
            hotelCountWithPromos: getHotelCountWithPromos(state),
            isAnchorHotelHidden: getIsAnchorHotelHidden(state)
          },
          analyticsContext: {
            [AnalyticsContext.SearchResultsContext]:
              action.payload?.searchResultsContext ||
              getSearchResultsContext(state)
          }
        })
      )
      break
    }

    case identifyAuthenticatedUser.type: {
      const user = getUser(state)
      const userId = getUserId(user)
      const userEmail = getUserEmail(user)
      const isPrivacyRestrictedUser = getIsPrivacyRestrictedUser(state)

      if (!userId) return

      // Following cases are implemented purely for GTM use-case. Please prevent duplicating this logic for other use-cases.
      const shouldIdentifyUserInGTM =
        userEmail && typeof window !== 'undefined' && window.dataLayer

      // If the user is not a privacy restricted user, we can identify them in GTM with the default event.
      if (shouldIdentifyUserInGTM && !isPrivacyRestrictedUser) {
        window.dataLayer?.push({
          event: 'UserIdentified',
          email: userEmail
        })
      }

      const cognitoTraits = getUserIdentifiableTraits(state)
      const memberPlus = getCognitoIsMemberPlus(user)
      const brandName = getBrandName(state)

      AnalyticsUtils.identify(userId, {
        memberPlus,
        user: cognitoTraits
      })
      try {
        await customIdentify({
          anonymousId,
          userId,
          customerDataEndpoint:
            process.env.NEXT_PUBLIC_ANALYTICS_CUSTOMER_DATA_ENDPOINT,
          customerDataApiKey:
            process.env.NEXT_PUBLIC_ANALYTICS_CUSTOMER_DATA_API_KEY,
          cognitoTraits,
          brandName
        })
      } catch (error) {
        logEvent(
          'CustomIdentifyEventFailed',
          {
            errorType: error?.message
          },
          'info'
        )
      }

      break
    }

    case TRACK_USER_ACTION: {
      const {
        params: {action: eventAction, category, label, meta}
      } = action

      const metadata = {
        action: eventAction,
        category,
        label,
        meta
      }
      // TODO: Search TS preexisting issue
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      AnalyticsUtils.trackEvent('UserAction', metadata)

      logEvent('UserAction', metadata)

      break
    }

    case TRACK_ERROR_TRIGGERED: {
      const {
        event: {errorType, errorMessage}
      } = action

      const metadata = {
        errorType,
        errorMessage
      }

      runInIdleTime(() => {
        // TODO: Search TS preexisting issue
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        AnalyticsUtils.trackEvent('ErrorTriggered', metadata)
      })

      logEvent('ErrorTriggered', metadata)

      break
    }

    case TRACK_EXPERIMENT_SERVED: {
      const {experimentId, variationId, isActive} = action
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Experiment,
          action: Action.Served,
          analyticsContext: {
            [AnalyticsContext.ExperimentContext]: {
              experimentId,
              variationId,
              isActive
            }
          }
        })
      )
      break
    }

    /** Log pending request for optimizely data file */
    case fetchOptimizelyDataFile.pending.type: {
      logTimeStart('FetchOptimizelyDataFile')
      break
    }

    /** Log success for optimizely data file */
    case fetchOptimizelyDataFile.fulfilled.type: {
      trackTimingEvent(
        dispatch,
        'FetchOptimizelyDataFile',
        'FetchOptimizelyDataFile-start'
      )
      break
    }

    /** Log error for optimizely data file */
    case fetchOptimizelyDataFile.rejected.type: {
      trackTimingEvent(
        dispatch,
        'FetchOptimizelyDataFile',
        'FetchOptimizelyDataFile-start'
      )

      break
    }

    /**
     * This is the new formalized way to track events
     * and should be used for all event tracking.
     */
    case trackEvent.type: {
      const payload = action.payload as TrackEventPayload

      const handleTrackEvent = () => {
        const topLevelEventName = createDataPipelineEventName(payload)
        const metadata = getEventProperties(state, payload)
        trackAnalyticsEvent(topLevelEventName, metadata)
      }

      const handleLogEvent = () => {
        const metadata = getEventProperties(state, payload)
        const ddMetaData = removeSearchResultsContext(metadata)

        // TODO: Search TS preexisting issue
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        logEvent(metadata.name, ddMetaData)
      }

      const handleTrackFullStoryEvent = () => {
        const topLevelEventName = createDataPipelineEventName(payload)
        const metadata = getEventProperties(state, payload)
        const fsMetaData = removeSearchResultsContext(metadata)
        // TODO: Search TS preexisting issue
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        trackFullStoryEvent(
          topLevelEventName,
          clearObjectArraysFromAnalyticsContext(
            fsMetaData
          ) as unknown as Record<string, unknown>
        )
      }

      if (getIsGhaChecker(state)) {
        handleTrackEvent()
        handleLogEvent()
        handleTrackFullStoryEvent()
      } else {
        await yieldToMain()

        runInIdleTime(handleTrackEvent)
        runInIdleTime(handleLogEvent)
        runInIdleTime(handleTrackFullStoryEvent)
      }

      break
    }

    case trackMapViewDisplayed.type: {
      logTimeStart('MapView')

      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.MapView,
          action: Action.Displayed
        })
      )

      break
    }

    case trackMapBoxLoaded.type: {
      trackMapBoxLoadedTiming(dispatch)

      break
    }

    // static map location
    case trackStaticLocationMapBoxDisplayed.type: {
      logTimeStart('StaticLocationMapView')

      break
    }

    case trackStaticLocationMapBoxLoaded.type: {
      trackStaticLocationMapBoxLoadedTiming(dispatch, action.payload.team)

      break
    }

    // dynamic map location
    case trackInteractiveLocationMapBoxDisplayed.type: {
      logTimeStart('InteractiveLocationMapView')
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.MapView,
          action: Action.Displayed,
          team: action.payload?.team
        })
      )

      break
    }

    case trackInteractiveLocationMapBoxLoaded.type: {
      trackInteractiveLocationMapBoxLoadedTiming(dispatch, action.payload.team)

      break
    }

    case trackProviderRedirect.type: {
      const params = getProviderRedirectParams(
        action.payload as TrackProviderRedirectParams
      )(state)

      // TODO: Search TS preexisting issue
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      AnalyticsUtils.trackEvent('ProviderRedirected', params)
      break
    }

    case hotelsAvailabilityReceived.type: {
      trackAnchorHotelFirstAvailableDatesTiming(dispatch)
      break
    }

    case hotelsAvailabilityCompleted.type: {
      trackAnchorHotelAllAvailableDatesTiming(dispatch)
      break
    }

    case trackAccommodationPageReady.type: {
      const startDate = localStorage.getItem('navigationToAccommodationPage')
      if (!startDate) return
      const start = Number(startDate)
      const now = Date.now()
      const isAPOverlay = location.pathname.includes('/Search')
      const entity = isAPOverlay ? Entity.APOverlay : Entity.APStandalone
      dispatch(
        trackEvent(getTrackTimingEventPayload('PageReady', now - start, entity))
      )
      break
    }

    case trackAccommodationPageLoad.type: {
      const startDate = localStorage.getItem('navigationToAccommodationPage')
      if (!startDate) return
      const start = Number(startDate)
      const now = Date.now()
      const isAPOverlay = location.pathname.includes('/Search')
      const entity = isAPOverlay ? Entity.APOverlay : Entity.APStandalone
      dispatch(
        trackEvent(getTrackTimingEventPayload('PageLoad', now - start, entity))
      )
      localStorage.removeItem('navigationToAccommodationPage')
      break
    }

    case markAccommodationPageLoad.type: {
      const {isRoomsFirstImageLoaded, isStaticMapImageLoaded} =
        state.accommodation
      if (!isStaticMapImageLoaded) break
      const brandHasRooms = getMetaBrandHasRooms(state)

      const unavailableRooms = getIsRoomsResultEmpty(state)
      const {rooms} = getFilteredRoomsData(state)
      const roomsWithImages = rooms.filter(room =>
        Boolean(room.images?.[0]?.url)
      )
      const hasRooms = brandHasRooms || unavailableRooms

      const hasImageLoadedOrNoImages =
        isRoomsFirstImageLoaded ||
        (rooms.length && !roomsWithImages.length) ||
        !hasRooms

      if (hasImageLoadedOrNoImages && isStaticMapImageLoaded)
        dispatch(trackAccommodationPageLoad())
      break
    }

    default: {
      break
    }
  }

  return next(action)
}
/* eslint-enable complexity */

export default middleware
